import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  injectBxIcons,
  barChart05Icon,
  clockIcon,
  clockStopwatchIcon,
  cpuChip01Icon,
  dataflow04Icon,
  rss01Icon,
} from '@beyonnex/bx-angular-ui';
import { formatDateTime } from '../../../models/core.models';
import { ThingLorawanGateway } from '../../../models/thing.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { isNullOrUndefined } from '../../../utils/utils';
import { CurrentStatePanelComponent } from '../current-state-panel/current-state-panel.component';
import { CurrentStateDataPoint } from '../current-state-panel/current-state-panel.models';
import { DeviceDetailsStore } from '../device-details.store';

@Component({
  selector: 'bxi-lorawan-current-state',
  imports: [CurrentStatePanelComponent],
  templateUrl: './lorawan-current-state.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LorawanCurrentStateComponent {
  private readonly store = inject(DeviceDetailsStore);
  protected readonly commonTranslate = inject(CommonTranslateService);

  protected readonly lorawanGateway = toSignal(this.store.lorawanGateway$);
  protected readonly data = computed<CurrentStateDataPoint[]>(() => {
    const gateway = this.lorawanGateway();
    const metrics = gateway?.features?.metrics?.properties;
    const comController = gateway?.features?.['com-controller']?.properties;

    return [
      {
        label: this.commonTranslate.translateThingProperty('features.metrics.properties.lnsConnectionMetrics.latencyMs'),
        value: this.commonTranslate.formatMilliseconds(metrics?.lnsConnectionMetrics?.latencyMs),
        // Deliberately not the `clock` blue used below: two clock-family icons in one panel must stay distinguishable.
        icon: 'clock-stopwatch',
        iconStyle: 'text-utility-purple-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.metrics.properties.cellularMetrics.cellularConnectionMetrics.cellId'),
        value: metrics?.cellularMetrics?.cellularConnectionMetrics?.cellId,
        icon: 'rss-01',
        iconStyle: 'text-utility-gray-blue-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.firmware.properties.version'),
        value: gateway?.features?.firmware?.properties?.version,
        icon: 'cpu-chip-01',
        iconStyle: 'text-utility-orange-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.analytics.properties.availability.availabilityPercentToday'),
        value: this.commonTranslate.formatPercent(gateway?.features?.analytics?.properties?.availability?.availabilityPercentToday),
        icon: 'bar-chart-05',
        iconStyle: 'text-teal-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.com-controller.properties.lastLoraTransmission'),
        value: formatDateTime(comController?.lastLoraTransmission),
        icon: 'clock',
        iconStyle: 'text-utility-blue-light-600',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.com-controller.properties.loraDevices'),
        value: this.getDeviceCount(gateway),
        icon: 'dataflow-04',
        iconStyle: 'text-utility-pink-500',
      },
    ];
  });

  constructor() {
    injectBxIcons([clockStopwatchIcon, rss01Icon, cpuChip01Icon, barChart05Icon, clockIcon, dataflow04Icon]);
  }

  private getDeviceCount(gateway?: ThingLorawanGateway): string | number | undefined {
    const count = gateway?.getLoraDeviceCount();
    if (isNullOrUndefined(count)) {
      return undefined;
    }

    const { LORA_DEVICE_LIST_LIMIT } = ThingLorawanGateway;

    return count >= LORA_DEVICE_LIST_LIMIT ? `${LORA_DEVICE_LIST_LIMIT}+` : count;
  }
}
